import { parseExpiryEpochSec, resolveInstanceUrl } from '../instance/urls.ts';
import type { ImageRef } from '../model/media.ts';
import type { VideoDetails } from '../model/video.ts';
import { err, ok, parseError, type ParseError, type Result } from '../result.ts';
import type { ParseContext } from './context.ts';
import { parseDescriptionNodes } from './richText.ts';
import { asBoolean, asIntLike, asObject, asString, field } from './raw.ts';
import { asEpochSeconds } from './time.ts';
import { parseSubtitleTrack } from './video.ts';

/**
 * The instance's own `/watch?v={id}` page, read as a *second source* for the same record
 * `/api/v1/videos/{id}` serves — for the videos where that endpoint answers `200` with zero bytes
 * (docs/api/video.md §2, the zero-byte answer). The page is rendered from the same instance but its player sources go
 * through the companion (`/companion/api/manifest/dash/…` with a signed `check=` token), which is
 * how a video the API has nothing for can still play.
 *
 * Split in two on the R4 boundary: {@link extractWatchPageFacts} is pure string work over the HTML
 * and hands the two embedded JSON documents back **as text**, because `JSON.parse` stays outside
 * core; the caller parses them and returns through {@link parseWatchPageDetails}, which assembles
 * a `VideoDetails` the rest of the pipeline (playback plan, chapters, the watch view model)
 * consumes unchanged.
 *
 * Regex over the markup rather than a DOM, for the reason `helpers.ts` reads the committed `.mpd`
 * that way: core has no DOM (R1), and the shapes matched here are three fixed templates —
 * `<script id="player_data">`/`<script id="video_data">` (measured 2026-08-24: `player_data`
 * carries the **full** description, byte-identical to the API's), the `<source>`/`<track>` list
 * inside the `<video>` element, and the `#channel-name`/`#views`/`#likes` metadata blocks.
 */

/** One `<source>` of the page's `<video>` element, verbatim apart from entity decoding. */
export interface WatchPageSourceRef {
  readonly url: string;
  readonly type: string;
}

/** One `<track kind="captions">` of the page's `<video>` element. */
export interface WatchPageTrackRef {
  readonly url: string;
  readonly label: string;
}

/**
 * What the page states outside its two JSON blocks, plus those blocks as raw text. Every field is
 * independently optional — a page that lost its byline still carries its sources, and vice versa —
 * so absence stays per-fact rather than failing the whole read (R4's list discipline, applied to a
 * document).
 */
export interface WatchPageFacts {
  /** Body of `<script id="player_data">` — title, full description, thumbnail path. */
  readonly playerDataJson: string | null;
  /** Body of `<script id="video_data">` — id, `length_seconds`, `live_now`, `premiere_timestamp`. */
  readonly videoDataJson: string | null;
  readonly sources: readonly WatchPageSourceRef[];
  readonly captionTracks: readonly WatchPageTrackRef[];
  readonly authorId: string | null;
  readonly authorName: string | null;
  readonly authorVerified: boolean;
  readonly viewCount: number | null;
  readonly likeCount: number | null;
}

/**
 * The named entities Invidious's templates emit into attribute values and text nodes. `&amp;`
 * last, so a literal `&amp;quot;` decodes to `&quot;` rather than to a bare quote.
 */
function decodeEntities(text: string): string {
  return text
    .replaceAll('&nbsp;', ' ')
    .replaceAll('&quot;', '"')
    .replaceAll('&#39;', "'")
    .replaceAll('&apos;', "'")
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&amp;', '&');
}

/**
 * One attribute out of one tag, either quote style — the same template mixes them
 * (`src="…" type='application/dash+xml'` on the DASH source, both double-quoted on the mp4 one).
 */
function attributeOf(tag: string, name: string): string | null {
  const match = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`).exec(tag);
  if (match === null) return null;
  const value = match[1] ?? match[2];
  return value === undefined ? null : decodeEntities(value);
}

function scriptBody(html: string, id: string): string | null {
  const match = new RegExp(`<script[^>]*\\bid=["']${id}["'][^>]*>([\\s\\S]*?)</script>`, 'i').exec(html);
  if (match === null) return null;
  const body = (match[1] ?? '').trim();
  return body === '' ? null : body;
}

/**
 * The digits of `#views` / `#likes`, which the page prints separator-formatted (`210,721`) with an
 * icon in front. Stripping everything but digits reads both and survives a separator change;
 * a block that holds no digits at all (`#dislikes` is served empty and hidden) reports `null`
 * rather than `0`, because "the page does not say" is not a count.
 */
function countOf(html: string, id: string): number | null {
  const match = new RegExp(`<p[^>]*\\bid=["']${id}["'][^>]*>([\\s\\S]*?)</p>`, 'i').exec(html);
  if (match === null) return null;
  const digits = (match[1] ?? '').replace(/<[^>]*>/g, '').replace(/\D/g, '');
  if (digits === '') return null;
  const value = Number(digits);
  return Number.isSafeInteger(value) ? value : null;
}

/**
 * Pulls the page apart into {@link WatchPageFacts}. Fails only when **both** JSON blocks are
 * absent, which is what the instance's error page looks like (measured 2026-08-24: a refused video
 * answers `500` with a `.h-box` sentence and neither block) — everything else degrades per fact.
 */
export function extractWatchPageFacts(html: string): Result<WatchPageFacts, ParseError> {
  const playerDataJson = scriptBody(html, 'player_data');
  const videoDataJson = scriptBody(html, 'video_data');
  if (playerDataJson === null && videoDataJson === null) {
    return err(parseError('WatchPage', null, 'missing'));
  }

  const sources: WatchPageSourceRef[] = [];
  for (const match of html.matchAll(/<source\b[^>]*>/gi)) {
    const url = attributeOf(match[0], 'src');
    const type = attributeOf(match[0], 'type');
    if (url !== null && type !== null) sources.push({ url, type });
  }

  const captionTracks: WatchPageTrackRef[] = [];
  for (const match of html.matchAll(/<track\b[^>]*>/gi)) {
    const kind = attributeOf(match[0], 'kind');
    if (kind !== 'captions' && kind !== 'subtitles') continue;
    const url = attributeOf(match[0], 'src');
    if (url === null) continue;
    captionTracks.push({ url, label: attributeOf(match[0], 'label') ?? '' });
  }

  /*
   * The byline: `#channel-name` holds the name (plus a checkmark icon on a verified channel), and
   * the `<a href="/channel/UC…">` that wraps it is the id. The *last* channel link before the name
   * is the wrapping one — matching the first link in the document would read a related-videos row
   * on a template that puts the sidebar first.
   */
  let authorId: string | null = null;
  let authorName: string | null = null;
  let authorVerified = false;
  const nameMatch = /<span[^>]*\bid=["']channel-name["'][^>]*>([\s\S]*?)<\/span>/i.exec(html);
  if (nameMatch !== null) {
    const inner = nameMatch[1] ?? '';
    const name = decodeEntities(inner.replace(/<[^>]*>/g, '')).trim();
    authorName = name === '' ? null : name;
    authorVerified = inner.includes('checkmark-circle');
    const before = html.slice(0, nameMatch.index);
    for (const link of before.matchAll(/<a href=["']\/channel\/([^"'?]+)["']/g)) {
      authorId = link[1] ?? authorId;
    }
  }

  return ok({
    playerDataJson,
    videoDataJson,
    sources,
    captionTracks,
    authorId,
    authorName,
    authorVerified,
    viewCount: countOf(html, 'views'),
    likeCount: countOf(html, 'likes'),
  });
}

/** {@link extractWatchPageFacts}'s output plus its two JSON blocks parsed — by the caller (R4). */
export interface WatchPageInput {
  /** The id the route asked for. The page has no reason to answer about a different one. */
  readonly videoId: string;
  readonly facts: WatchPageFacts;
  readonly playerData: unknown;
  readonly videoData: unknown;
}

/**
 * A `VideoDetails` from what the page states. Fields the page does not carry take the same
 * defaults `parseVideoDetails` gives a payload that omits them — `null`, empty, or the safe
 * boolean — so everything downstream reads "the record does not say" in the shape it already
 * speaks: no view count hides the counter, no `published` hides the date, an empty
 * `recommended` leaves the rail unrendered.
 *
 * Fails only when neither JSON block parsed to an object: a page with both blocks unreadable is
 * not this template, whatever else matched.
 */
export function parseWatchPageDetails(input: WatchPageInput, ctx: ParseContext): Result<VideoDetails, ParseError> {
  const player = asObject(input.playerData);
  const video = asObject(input.videoData);
  if (player === null && video === null) {
    return err(parseError('WatchPage', null, 'malformed'));
  }

  const description = (player === null ? null : asString(field(player, 'description'))) ?? '';
  const lengthSeconds = (video === null ? null : asIntLike(field(video, 'length_seconds'))) ?? 0;

  /*
   * The page's `<video>` sources, by MIME type. Only the mp4 variant carrying `local=true` is the
   * progressive fallback — the page also lists the unproxied one, which §2's trap applies to
   * unchanged (IP-locked to the instance's egress). No `local=true` is forced onto the manifest
   * URLs here, unlike `parseVideoDetails`'s `resolveManifestUrl`: the page emits the form its own
   * player uses, `check=` token included, and rewriting a signed URL invalidates it.
   */
  let dashUrl: string | null = null;
  let hlsUrl: string | null = null;
  let progressiveFallbackUrl: string | null = null;
  let mediaExpiresAtEpochSec: number | null = null;
  for (const source of input.facts.sources) {
    const resolved = resolveInstanceUrl(ctx.config, source.url);
    if (resolved === null) continue;
    const expiry = parseExpiryEpochSec(resolved);
    if (expiry !== null && (mediaExpiresAtEpochSec === null || expiry < mediaExpiresAtEpochSec)) {
      mediaExpiresAtEpochSec = expiry;
    }
    const type = source.type.toLowerCase();
    if (type.startsWith('application/dash+xml')) {
      dashUrl ??= resolved;
    } else if (type.startsWith('application/x-mpegurl')) {
      hlsUrl ??= resolved;
    } else if (type.startsWith('video/mp4') && /[?&]local=true(?:&|$)/.test(resolved)) {
      progressiveFallbackUrl ??= resolved;
    }
  }

  // `parseSubtitleTrack` unchanged: the page's track URLs are the companion caption path with the
  // label in the query, which is exactly the shape it resolves (§2 trap 1) — and its label-based
  // auto-generated reading is the only language signal the page offers, there being no
  // `language_code` anywhere in the markup.
  const subtitles = [];
  const dropped: ParseError[] = [];
  for (const track of input.facts.captionTracks) {
    const parsed = parseSubtitleTrack({ url: track.url, label: track.label }, ctx);
    if (parsed.ok) subtitles.push(parsed.value);
    else dropped.push(parsed.error);
  }

  const thumbnailPath = player === null ? null : asString(field(player, 'thumbnail'));
  const thumbnailUrl = thumbnailPath === null ? null : resolveInstanceUrl(ctx.config, thumbnailPath);
  const thumbnails: ImageRef[] =
    thumbnailUrl === null ? [] : [{ url: thumbnailUrl, width: null, height: null, quality: null }];

  return ok({
    videoId: input.videoId,
    title: (player === null ? null : asString(field(player, 'title'))) ?? '',
    description,
    // No mention→channel map: the page's rendered description is not `descriptionHtml`, and R8's
    // `mention` carries both targets nullable for exactly this kind of source.
    descriptionNodes: parseDescriptionNodes(description, '', { durationSec: lengthSeconds }),
    author: input.facts.authorName ?? '',
    authorId: input.facts.authorId,
    authorUrl: input.facts.authorId === null ? null : `/channel/${input.facts.authorId}`,
    authorVerified: input.facts.authorVerified,
    authorThumbnails: [],
    publishedEpochSec: null,
    lengthSeconds,
    viewCount: input.facts.viewCount,
    likeCount: input.facts.likeCount,
    dislikeCount: null,
    rating: null,
    genre: null,
    keywords: [],
    isFamilyFriendly: true,
    allowedRegions: [],
    isListed: true,
    allowRatings: true,
    paid: false,
    premium: false,
    liveNow: (video === null ? null : asBoolean(field(video, 'live_now'))) ?? false,
    isUpcoming: false,
    isPostLiveDvr: false,
    premiereAtEpochSec: video === null ? null : asEpochSeconds(field(video, 'premiere_timestamp')),
    thumbnails,
    storyboard: null,
    subtitles,
    dashUrl,
    hlsUrl,
    progressiveFallbackUrl,
    mediaExpiresAtEpochSec,
    recommended: [],
    dropped,
  });
}
